
import { Vue, Component, Prop } from "vue-property-decorator";

import UPDATE_GROUP from "@/apollo/teams/UpdateGroup.gql";
import CREATE_GROUP from "@/apollo/teams/CreateGroup.gql";

import WindowAlerts from "@/components/alert/window-alerts.vue";
import FormGroup from "@/components/form/FormGroupNew.vue";
import UiButton from "@/components/button/ui-button.vue";
import ContractorsMultiselect from "@/components/autocompleteMultiselects/ContractorsMultiselectNew.vue";
import SkillsMultiselect from "@/components/autocompleteMultiselects/SkillsMultiselectNew.vue";
import ProjectsMultiselect from "@/components/autocompleteMultiselects/ProjectsMultiselectV2.vue";
import PostingsMultiselect from "@/components/autocompleteMultiselects/PostingsMultiselectNew.vue";
import ModalFullScreen from "@/components/modal/ModalFullScreenNew.vue";
import ManagersMultiselect from "@/components/autocompleteMultiselects/ManagersMultiselectNew.vue";
import ModalHeader from "@/components/modal/ModalHeader.vue";

import { getDeletedAddedElements } from "@/helpers/GroupModalHelpers/UpdatedArrayDifference";
import getContractorsMultiselectConditions from "@/helpers/getContractorsMultiselectConditions";
import Message from "@/data/notificationsMessages";

import Roles from "@/entities/Roles";
import DropdownOption from "@/entities/DropdownOption";
import { NOTIFICATION } from "@/entities/Events";
import { User, Team } from "@/generated/graphql";
import ModalSectionsDivider from "@/components/modal/ModalSectionsDivider.vue";
import CustomSkillsMultiselect from "@/components/autocompleteMultiselects/customOptions/CustomSkillsMultiselectNew.vue";
import ModalContent from "@/components/modal/ModalContentNew.vue";

interface Group {
  id: string;
  name: string | null;
  location: string | null;
  active: boolean;
  managersIds: string[];
  contractorsIds: string[];
  skills: any[];
  postings: any[];
  projects: any[];
}

@Component({
  components: {
    ModalContent,
    CustomSkillsMultiselect,
    ModalFullScreen,
    FormGroup,
    UiButton,
    ContractorsMultiselect,
    SkillsMultiselect,
    ProjectsMultiselect,
    PostingsMultiselect,
    ManagersMultiselect,
    WindowAlerts,
    ModalHeader,
    ModalSectionsDivider,
  },
})
export default class GroupModal extends Vue {
  @Prop({ type: Object, default: null }) readonly data?: Team;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Boolean, default: false }) readonly dialog!: boolean;
  @Prop({ type: String, required: true }) readonly companyId!: string;

  valid: boolean = false;
  newSkillsNames: string[] = [];

  noContractorsMessage: string =
    "In order to be able to create groups you need access to contractors. Search in the Talent Directory and request access from the Admin";
  alerts: any = [{ code: 2, message: this.noContractorsMessage }];

  group: Group = {
    id: "",
    name: null,
    location: null,
    active: true,
    managersIds: [],
    contractorsIds: [],
    skills: [],
    postings: [],
    projects: [],
  };

  original: any = {};

  mounted() {
    if (this.data) {
      this.original = { ...this.data };
      this.group = {
        ...this.group,
        ...this.data,
        skills: this.data.skills.map((item: any) => item.skill.id),
        contractorsIds: this.data.contractors.map((item) => item.contractor.id),
        managersIds: this.data.managers.map((item: any) => item.manager.id),
        postings: this.data.postings.map((item: any) => item.posting),
        projects: this.data.projects.map((item: any) => item.project.id),
      };
    }
  }

  updateSkills(skillsIds: number[], newSkillsNames: string[]): void {
    this.group.skills = skillsIds;
    this.newSkillsNames = newSkillsNames;
  }

  handleSubmit(): void {
    if (this.data) {
      this.handleUpdate();
    } else {
      this.handleCreate();
    }
  }

  closeModal() {
    this.reset();
    this.$emit("close");
  }

  reset() {
    this.group = {
      ...this.group,
      name: null,
      location: null,
      active: false,
      managersIds: [],
      contractorsIds: [],
      skills: [],
      postings: [],
      projects: [],
    };
  }

  get modalTitle(): string {
    const mode = this.data ? "Edit" : "Create";
    return `${mode} Group`;
  }

  get hideContractorsMessage(): boolean {
    if (this.currentUser.role == Roles.MANAGER) {
      return !!this.currentUser.contracts.length;
    }
    return false;
  }

  get showManagersFilter(): boolean {
    return this.currentUser.role == Roles.ADMIN;
  }

  get contractorRules(): string {
    return this.currentUser.role == Roles.ADMIN ? "" : "required_field";
  }

  get contractorsTitle(): string {
    return !!this.contractorRules ? `Contractors` : "Contractors";
  }

  get contractorsIds(): string[] {
    if (this.currentUser.role == Roles.MANAGER) {
      return (
        this.currentUser.manager?.contractors?.map(
          (c: any) => c.contractor_id
        ) || []
      );
    }
    return [];
  }

  get postingsWhere(): object {
    if (this.currentUser.role == Roles.ADMIN) {
      return {
        manager_id: {
          _is_null: false,
        },
      };
    }
    if (this.currentUser.role == Roles.MANAGER) {
      return {
        applications: {
          user: {
            contracts: {
              id: {
                _in: this.contractorsIds,
              },
            },
          },
        },
      };
    }
    return {};
  }

  get projectsWhere(): any[] {
    // if (this.currentUser.role == Roles.ADMIN) {
    //   return [
    //     {
    //       manager_id: {
    //         _is_null: false,
    //       },
    //     },
    //   ];
    // }
    if (this.currentUser.role == Roles.MANAGER) {
      return [
        {
          project_activities: {
            contractor_id: {
              _in: this.contractorsIds,
            },
          },
        },
      ];
    }
    return [];
  }

  get contractorsWhere(): object {
    return getContractorsMultiselectConditions(this.currentUser.role);
  }

  get selectedContractorsIds(): string[] {
    return this.group.contractorsIds;
  }

  handleContractorInput(contractorIds: string[]): void {
    this.group.contractorsIds = contractorIds;
  }

  get managersIds(): string[] {
    return this.group.managersIds;
  }

  handleManagerInput(managersIds: string[]): void {
    this.group.managersIds = managersIds;
  }

  get chosenSkillsIds(): number[] {
    if (!this.group.skills.length) {
      return [];
    }

    return this.group.skills;
  }

  handleSkillInput(val: string[]): void {
    this.group.skills = val.length ? val : [];
  }

  get chosenProjectsIds(): DropdownOption[] {
    if (!this.group.projects.length) {
      return [];
    }

    return this.group.projects;
  }

  handleProjectInput(val: string[]): void {
    this.group.projects = val.length ? val : [];
  }

  get postingValue(): DropdownOption[] {
    if (!this.group.postings.length) {
      return [];
    }
    return this.group.postings.map((posting: any) => {
      return { id: posting.id, name: posting.job_title };
    });
  }

  handlePostingInput(val: DropdownOption[]): void {
    if (!val.length) {
      this.group.postings = [];
    }

    this.group.postings = val.map((v: any) => {
      return {
        id: v.id,
        job_title: v.name,
      };
    });
  }

  async handleUpdate(): Promise<void> {
    const originalContractorsIds = this.original.contractors.map(
      (c: any) => c.contractor.id
    );
    const updatedContractorsIds = this.group.contractorsIds;

    const originalSkillsIds = this.original.skills.map((s: any) => s.skill.id);
    const updatedSkillsIds = this.group.skills;

    const originalProjectsIds = this.original.projects.map(
      (p: any) => p.project.id
    );
    const updatedProjectsIds = this.group.projects;

    const originalPostingsIds = this.original.postings.map(
      (p: any) => p.posting.id
    );
    const updatedPostingsIds = this.group.postings.map((p: any) => p.id);

    const originalManagersIds = this.original.managers.map(
      (m: any) => m.manager.id
    );
    const updatedManagersIds = this.group.managersIds;

    const contractorObjects = getDeletedAddedElements(
      originalContractorsIds,
      updatedContractorsIds,
      this.group.id,
      "contractor_id"
    );

    const skillsObjects = getDeletedAddedElements(
      originalSkillsIds,
      updatedSkillsIds,
      this.group.id,
      "skill_id"
    );

    const projectsObjects = getDeletedAddedElements(
      originalProjectsIds,
      updatedProjectsIds,
      this.group.id,
      "project_id"
    );

    const postingsObjects = getDeletedAddedElements(
      originalPostingsIds,
      updatedPostingsIds,
      this.group.id,
      "posting_id"
    );

    const managersObjects = getDeletedAddedElements(
      originalManagersIds,
      updatedManagersIds,
      this.group.id,
      "manager_id"
    );

    const groupGeneralSet = {
      name: this.group.name,
      location: this.group.location,
    };

    const groupId = this.group.id;
    try {
      await this._updateGroup(
        groupGeneralSet,
        contractorObjects.deleted,
        contractorObjects.added,
        projectsObjects.deleted,
        projectsObjects.added,
        postingsObjects.deleted,
        postingsObjects.added,
        skillsObjects.deleted,
        skillsObjects.added,
        managersObjects.deleted,
        managersObjects.added,
        groupId
      );
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage("Updated successfully")
      );
       this.$emit("refetch");
      this.closeModal();
    } catch (err) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(err.message));
    }
  }

  async _updateGroup(
    groupGeneralSet: any,
    contractorsToDelete: any,
    contractorsToAdd: any,
    projectsToDelete: any,
    projectsToAdd: any,
    postingsToDelete: any,
    postingsToAdd: any,
    skillsToDelete: any,
    skillsToAdd: any,
    managersToDelete: any,
    managersToAdd: any,
    groupId: any
  ) {
    return await this.$apollo.mutate({
      mutation: UPDATE_GROUP,
      variables: {
        groupGeneralSet,
        contractorsToDelete,
        contractorsToAdd,
        projectsToDelete,
        projectsToAdd,
        postingsToDelete,
        postingsToAdd,
        skillsToDelete,
        skillsToAdd,
        managersToDelete,
        managersToAdd,
        groupId,
        customSkills: this.newSkillsNames.map((skillName) => ({
          team_id: groupId,
          skill: {
            data: {
              user_id: this.currentUser.id,
              name: skillName,
            },
          },
        })),
      },
    });
  }

  async handleCreate(): Promise<void> {
    try {
      const contractorsData = this.group.contractorsIds.map((contractorId) => {
        return {
          contractor_id: contractorId,
        };
      });

      const projectsData = this.group.projects.map((projectId: string) => {
        return {
          project_id: projectId,
        };
      });

      const postingsData = this.group.postings.map((p: any) => {
        return {
          posting_id: p.id,
        };
      });

      const skillsData = this.group.skills.map((skillId: string) => {
        return {
          skill_id: Number(skillId),
        };
      });

      const newSkills = this.newSkillsNames.map((skillName) => ({
        skill: {
          data: {
            name: skillName,
            user_id: this.currentUser.id,
          },
        },
      }));

      const managersData = this.group.managersIds.map((managerId) => {
        return {
          manager_id: managerId,
        };
      });

      const createGroupObject = {
        archived: false,
        company_id: this.companyId,
        location: this.group.location,
        name: this.group.name,
        created_by: this.currentUser.id,
        contractors: {
          data: contractorsData,
        },
        postings: {
          data: postingsData,
        },
        projects: {
          data: projectsData,
        },
        skills: {
          data: [...skillsData, ...newSkills],
        },
        managers: {
          data: managersData,
        },
      };
      await this._createGroup(createGroupObject);
      this.$emit("refetch");
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage("Created successfully")
      );
      this.closeModal();
    } catch (err) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(err.message));
    }
  }

  async _createGroup(createGroupObject: any) {
    return await this.$apollo.mutate({
      mutation: CREATE_GROUP,
      variables: {
        createGroupObject,
      },
    });
  }

  onSkillAdded(skillId: string): void {
    this.group = {
      ...this.group,
      skills: [...this.group.skills, skillId],
    };
  }
}
